import { isNil } from 'lodash';
import { MBIconV2Name } from '@shared/components/image';
import { i18n } from '@shared/util/i18n';
import { v4 } from '@shared/util/uuid';
import {
  STATIC_COMPONENT_IDS,
  defaultComponentTemplateOptions,
} from './template/ComponentOptionDefaults';
import { isInputComponent, isManagedComponent, isRelatedCardComponent } from './utils';

export type WorkflowView = Omit<MBAPI.ViewTemplate, 'createdAt' | 'updatedAt'>;

export type GenericComponent = MBAPI.WorkflowTemplate_components;

export type GenericOverride = MBAPI.ViewTemplate_componentsViewOptions_overrides;

export type NumberComponent = MBAPI.WorkflowTemplate_components_NumberComponent;

export type RelatedCardComponent = MBAPI.WorkflowTemplate_components_RelatedCardComponent;

export type RelatedCardLookupComponent =
  MBAPI.WorkflowTemplate_components_RelatedCardLookupComponent;

export type ScheduledAutomationComponent =
  MBAPI.WorkflowTemplate_components_ScheduledAutomationComponent;

export type DateComponent = MBAPI.WorkflowTemplate_components_DateComponent;

export type TagComponent = MBAPI.WorkflowTemplate_components_TagComponent;

export type PersonComponent = MBAPI.WorkflowTemplate_components_PersonComponent;

export type NumberFormat = NumberComponent['format'];

export type TextComponent = MBAPI.WorkflowTemplate_components_TextComponent;

export type AuditTagComponent = MBAPI.WorkflowTemplate_components_AuditTagComponent;

export type CombinedTagsComponent = MBAPI.WorkflowTemplate_components_CombinedTagsComponent;

export type TimeTrackerComponent = MBAPI.WorkflowTemplate_components_TimeTrackerComponent;

export type ReferencedInComponent = MBAPI.WorkflowTemplate_components_ReferencedInComponent;

export type UrlComponent = MBAPI.WorkflowTemplate_components_UrlComponent;

export function prepareComponent(component: Partial<GenericComponent>): GenericComponent {
  return {
    id: component.id || v4(),
    ...component,
  } as GenericComponent;
}

export const componentTemplateOptions = defaultComponentTemplateOptions;

export const availableFormats: NumberFormat[] = ['FLOAT', 'CURRENCY', 'PERCENT'];

export function iconForNumberFormat(format: NumberComponent['format']): MBIconV2Name {
  switch (format) {
    case 'PERCENT':
      return 'percent';
    case 'CURRENCY':
      return 'money-dollar-circle';
    case 'FLOAT':
    default:
      return 'hashtag';
  }
}

export function titleForNumberFormat(format: NumberFormat) {
  return i18n(`workflowTemplate.numberFormat.${format || 'FLOAT'}`);
}

// Temporarily using the types from Card Definitions
export function getIconForView(
  view: { slug?: string; title?: string; context?: string },
  viewTemplates: MBAPI.ViewTemplate[],
): MBIconV2Name {
  const viewTemplate = viewTemplates?.find(item => item.id === view.slug);
  if (!isNil(viewTemplate)) {
    switch (viewTemplate.subtype) {
      case 'BOARD':
        return 'board';
      case 'CALENDAR':
        return 'calendar';
      case 'TABLE':
        return 'table-grid';
      default:
        return 'list';
    }
  }
  if (view.context === 'agenda') {
    return 'calendar';
  } else if (view.context === 'kanban') {
    return 'board';
  } else {
    return 'list';
  }
}

function getShouldHideComponentsByDefault(subtype: WorkflowView['subtype']): boolean {
  return ['LIST', 'CALENDAR', 'BOARD'].includes(subtype ?? '');
}

function getShouldHideLabelsByDefault(subtype: WorkflowView['subtype']): boolean {
  return ['TABLE', 'CALENDAR', 'BOARD'].includes(subtype ?? '');
}

type ViewInput = Pick<
  WorkflowView,
  | 'name'
  | 'workflowTemplateId'
  | 'subtype'
  | 'calendarDateComponentId'
  | 'checklistTagComponentId'
  | 'customSortingEnabled'
>;

export function prepareViewData(
  input: ViewInput,
  visibleComponents: MBAPI.ViewTemplate_componentsViewOptions[],
): WorkflowView {
  return {
    id: v4(),
    type: 'COLLECTION',
    isPublic: false,
    defaultHiddenComponents: getShouldHideComponentsByDefault(input.subtype),
    defaultHiddenLabels: getShouldHideLabelsByDefault(input.subtype),
    defaultReadonlyComponents: true,
    displayInCards: false,
    displayLegacyRecurrence: false,
    displayLegacyReminders: false,
    displayLegacyStatusButton: false,
    displayMessageThread: false,
    summarizeReadonlyTags: true,
    sortOrder: null,
    collectionCriteria: {
      sortByComponents: null,
      filterByComponents: null,
      groups: null,
      // TODO if saving
      // omniSearchQuery
    },
    componentsViewOptions: visibleComponents,
    ...input,
  };
}

export function getAvailableViewSubtypes(): WorkflowView['subtype'][] {
  return ['LIST', 'BOARD', 'TABLE', 'CALENDAR'];
}

export function getViewSubtypeName(subtype: WorkflowView['subtype']): string {
  switch (subtype) {
    case 'BOARD':
      return i18n('workflowTemplate.viewTypeLabels.board');
    case 'CALENDAR':
      return i18n('workflowTemplate.viewTypeLabels.calendar');
    case 'TABLE':
      return i18n('workflowTemplate.viewTypeLabels.table');
    case 'LIST':
    default:
      return i18n('workflowTemplate.viewTypeLabels.list');
  }
}

export function getIconForViewSubtype(subtype: WorkflowView['subtype']): MBIconV2Name {
  switch (subtype) {
    case 'BOARD':
      return 'board';
    case 'CALENDAR':
      return 'calendar';
    case 'TABLE':
      return 'table-grid';
    case 'LIST':
    default:
      return 'list';
  }
}

export const defaultViewSubtypeSelected: WorkflowView['subtype'] = 'LIST';

function isGroupableRelatedCardComponent(component: GenericComponent): boolean {
  return isRelatedCardComponent(component);
}

export function isComponentGroupable(component: GenericComponent): boolean {
  const GROUPABLE_TYPES: Array<GenericComponent['type']> = ['TAG'];
  const GROUPABLE_IDS: Array<GenericComponent['id']> = ['assignees'];
  return (
    GROUPABLE_TYPES.includes(component.type) ||
    GROUPABLE_IDS.includes(component.id) ||
    isGroupableRelatedCardComponent(component)
  );
}

export function isViewGroupable(view: WorkflowView): boolean {
  return view.type === 'COLLECTION' && view.subtype !== 'CALENDAR';
}

const LIST_VIEW_DEFAULT_TYPES = ['TAG', 'PERSON', 'RELATED_CARD', 'COMBINED_TAGS', 'DATE'];
const MAX_LIST_VIEW_DEFAULT_COMPONENTS = 5;

export function getDefaultListViewComponentIds(
  workflowTemplate?: MBAPI.WorkflowTemplate,
): MBAPI.ViewTemplate_componentsViewOptions[] {
  if (isNil(workflowTemplate)) {
    return [];
  }
  return [
    {
      componentId: STATIC_COMPONENT_IDS.NAME,
      overrides: {
        type: 'TEXT',
        isHidden: false,
      },
    } as MBAPI.ViewTemplate_componentsViewOptions,
    ...workflowTemplate.components
      .filter(component => LIST_VIEW_DEFAULT_TYPES.includes(component.type))
      .filter(c => !isInputComponent(c) || c.isHidden !== true)
      .filter(c => !isManagedComponent(c))
      .slice(0, MAX_LIST_VIEW_DEFAULT_COMPONENTS)
      .map(
        ({ id, type }) =>
          ({
            componentId: id,
            overrides: {
              type,
              isHidden: false,
            },
          }) as MBAPI.ViewTemplate_componentsViewOptions,
      ),
  ];
}
